import React from 'react'
import { Bullet, Bullets, Info, Page, Section, Warning } from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const EPCRMobile = () => (
  <Page
    title="ePCR on the Mobile App"
    intro="Crews can complete ePCRs on the AmbuNet mobile app, including offline. The mobile record covers the same modules as the web app and syncs to the server whenever a connection is available."
  >
    <Section title="Drafts and offline working">
      <Bullets>
        <Bullet>
          The mobile ePCR screen lists your drafts with a sync status badge on
          each, so you can see whether a record has reached the server. Drafts
          created on the device keep working with no signal and sync when
          you are back online.
        </Bullet>
        <Bullet>
          Server drafts you started elsewhere (for example on a web
          workstation) can be picked up and continued on the device, and your
          recent ePCRs are listed just like on the web dashboard.
        </Bullet>
        <Bullet>
          Every record type your organisation has enabled is available —
          full ePCR, Fast Track, Welfare, and Critical Care. Critical Care
          records include the live tap-to-log console and the dedicated
          cardiac arrest screen on the device. Record types are switched on
          per organisation in Site Configuration → Patient Records.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Sync conflicts and records that change hands">
      <p>
        Because a record can be edited on the device while the server copy
        moves on, the app reconciles the two whenever it syncs:
      </p>
      <Bullets>
        <Bullet>
          <strong>Sync conflicts</strong> — if the server copy changed since
          the device last saw it, a conflict screen lists each differing
          field and lets you pick which value wins. Fields that are blank on
          the server are resolved automatically in favour of the device, so
          the typical case (a draft that synced empty and was then filled in
          offline) never needs a decision.
        </Bullet>
        <Bullet>
          <strong>Records transferred away</strong> — when a sync is refused,
          the app checks whether the record is still yours. A copy with no
          unsaved work is removed from the device with a notice explaining
          why (transferred, submitted elsewhere, handed to a treatment
          location, or deleted). A copy <em>with</em> unsaved work is never
          discarded automatically — it is kept on the device as a{' '}
          <strong>detached</strong> record, with a notice, for you to open
          and decide: upload it again (as a new record, where the original
          was deleted) or discard it.
        </Bullet>
        <Bullet>
          <strong>Duplicate protection</strong> — if a record is created
          while the connection drops mid-save, the app recognises its own
          server copy on the next sync and links back to it rather than
          creating a duplicate record.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="What crews can record on mobile">
      <p>
        A mobile record has the same module tabs as the web sidebar: Incident,
        Demographics, PMHx, History, Assess, Vitals, Treatment, Attachments,
        Plan, Safeguarding, Sharing, and Review. In particular:
      </p>
      <Bullets>
        <Bullet>
          <strong>Demographics</strong> — includes the participant lookup:
          when the linked event has an entry list uploaded, crews can search
          it by bib number and pre-fill the patient&apos;s details after a
          confirmation popup, just like on the web. The search itself needs an
          internet connection. See{' '}
          <DocLink
            docsPage
            url="/clinical-records/starting-and-finding"
            text="Starting and Finding ePCRs"
          />
          .
        </Bullet>
        <Bullet>
          <strong>Vitals</strong> — full observation sets including capillary
          refill time (CRT), with the NEWS2 score (or National PEWS score for
          patients under 18) calculated and colour-banded per set using the
          same calculation as the web app.
        </Bullet>
        <Bullet>
          <strong>Assess</strong> — the full assessment module set, including
          body maps drawn on the device and point of care testing (POC) with
          the same tests and fields as the web app.
        </Bullet>
        <Bullet>
          <strong>Treatment</strong> — structured forms for each treatment
          module (airway, oxygen, catastrophic haemorrhage, splinting,
          sedation, and more), not just free-text notes, plus a dedicated
          cardiac arrest screen. Assessment and treatment entries can be
          back-dated to the time they actually happened.
        </Bullet>
        <Bullet>
          <strong>Safeguarding</strong> — the same concern questions as the
          web app, in a dedicated tab. See{' '}
          <DocLink
            docsPage
            url="/clinical-records/safeguarding-and-health-safety"
            text="Safeguarding and Health & Safety"
          />
          .
        </Bullet>
        <Bullet>
          <strong>Sharing</strong> — share codes can be created, emailed, and
          revoked, and the record can be transferred to another clinician.
          Sharing tools need an internet connection because they act on the
          server copy of the record.
        </Bullet>
      </Bullets>
      <Info title="Option lists match the web app">
        Choices such as gender, patient outcome, destination type, and health
        and safety incident types use the same option lists on mobile and
        web, so records read consistently wherever they were written.
      </Info>
    </Section>

    <Section title="Deleting a record on mobile">
      <p>
        A draft can be deleted from the device. A record that has never
        synced is simply removed; once it has reached the server, deleting it
        needs an internet connection because the server copy must be removed
        too — and the server&apos;s rules apply, so records that already hold
        patient details can only be deleted by a manager through{' '}
        <DocLink
          docsPage
          url="/clinical-records/reviewing-and-managing"
          text="Patient Report Forms"
        />
        .
      </p>
      <Warning title="Detached copies are local only">
        Deleting a detached record only discards the copy on the device — the
        server&apos;s version of that record (where one still exists) is
        untouched.
      </Warning>
    </Section>

    <Section title="What stays on the web app">
      <Bullets>
        <Bullet>
          Practice mode and the governance review tools (Patient Report Forms
          search, safeguarding and H&amp;S review, PDF downloads) are
          web-only.
        </Bullet>
        <Bullet>
          Access logs for share codes are viewed on the web record, not on
          the device.
        </Bullet>
        <Bullet>
          Imaging requests (requesting studies, approvals, impressions, and
          radiation dose tracking) are web-only for now — see{' '}
          <DocLink
            docsPage
            url="/clinical-records/imaging-requests"
            text="Imaging Requests"
          />
          .
        </Bullet>
      </Bullets>
    </Section>
  </Page>
)

export default EPCRMobile
